import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [selectVal, setSelectVal] = useState('');
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (selectVal) {
      window.open(selectVal, '_blank', 'noopener,noreferrer');
      setSelectVal('');
      if (selectRef.current) selectRef.current.value = '';
    }
  }, [selectVal]);

  return (
    <section id="categories" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="lg:max-w-xl">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-3">
              Curated
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-900 leading-tight">
              {t('home.categories.title')}
            </h2>
          </div>
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed lg:max-w-md">
            {t('home.categories.subtitle')}
          </p>
        </div>

        <div className="mb-10">
          <div className="relative max-w-md">
            <select
              ref={selectRef}
              value={selectVal}
              onChange={(e) => setSelectVal(e.target.value)}
              className="w-full appearance-none bg-background-100 border border-background-300/60 rounded-xl px-5 py-3.5 pr-12 text-foreground-800 text-sm font-medium focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all duration-200 cursor-pointer"
            >
              <option value="" disabled>
                {t('home.categories.placeholder')}
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.url}>
                  {t(`categories.${cat.id}`)}
                </option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <i className="ri-arrow-down-s-line text-foreground-500 text-lg"></i>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group relative bg-white border border-background-200/70 rounded-xl p-4 md:p-5 hover:border-primary-300/50 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer flex flex-col items-center text-center"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-background-100 flex items-center justify-center mb-3 group-hover:bg-primary-100/50 transition-colors duration-300">
                <CategoryIcon id={cat.id} />
              </div>
              <span className="text-xs md:text-sm font-medium text-foreground-800 group-hover:text-foreground-900 transition-colors duration-300">
                {t(`categories.${cat.id}`)}
              </span>
              <i className="ri-arrow-right-up-line text-xs text-foreground-400 opacity-0 group-hover:opacity-100 transition-all duration-300 absolute top-3 right-3"></i>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryIcon({ id }: { id: string }) {
  const map: Record<string, string> = {
    shoes: 'ri-footprint-line',
    hoodies: 'ri-shirt-line',
    tshirts: 'ri-t-shirt-2-line',
    jackets: 'ri-store-line',
    pants: 'ri-shopping-bag-line',
    headwear: 'ri-user-3-line',
    sets: 'ri-stack-line',
    underwear: 'ri-shirt-line',
    jersey: 'ri-football-line',
    accessories: 'ri-glasses-line',
    bags: 'ri-briefcase-line',
    belts: 'ri-vip-line',
    perfumes: 'ri-drop-line',
    other: 'ri-more-line',
  };
  return (
    <i
      className={`text-xl md:text-2xl text-foreground-700 group-hover:text-primary-600 transition-colors duration-300 ${map[id] ?? 'ri-price-tag-3-line'}`}
    ></i>
  );
}